import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, ModalSubTitle } from '../comps';
import { FormikProvider, useFormik } from 'formik';
import { ActionsModalSkeleton, Poppins, Spacer } from 'src/common';
import { Guard } from '../Guard';
import {
  EventCompletionStatus,
  EventInput,
  EventKind,
  ObjectKind,
  useEventQuery,
  useUpsertEventMutation,
} from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';
import { selectUserOption } from 'src/utils/misc';
import { isQueryLoading, useCache } from 'src/graphql';
import { queryCache } from 'react-query';
import _ from 'lodash';
import { ActiveTab } from './types';
import TabsMenu from 'src/components/TabsMenu';
import { FromCSV } from './forms';
import { FromNew } from './forms/FromNew/FromNew';
import { validationSchema } from './util';
import moment from 'moment-timezone';

const StyledModal = styled(Modal)`
  width: 950px;
  padding: 0 !important;
  max-height: calc(100% - 40px);
  overflow-y: auto;

  .action-text {
    cursor: pointer;
    text-decoration: underline;
    text-decoration-line: underline;
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;
  }
`;

const ErrorLabel = styled.div`
  margin-top: 15px;
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.error};
  font-weight: 400;
`;

interface ActionsModalProps {
  objectType: ObjectKind;
  objectId: string;
  name: string;
  onClose: () => void;
  eventId: string | null;
  eventIds: string[];
}

export const ActionsModal: React.FC<ActionsModalProps> = ({
  onClose,
  objectType,
  name,
  objectId,
  eventId,
  eventIds,
}) => {
  const { workspaceId, usersOptios } = useAuth();
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const { refetchQuery } = useCache();
  const [activeTab, setActiveTab] = useState<ActiveTab>('from_new');
  const [file, setFile] = useState<File | null>(null);
  const [readyForUpload, setReadyForUpload] = useState(false);
  const [isCsvDirty, setIsCsvDirty] = useState(false);

  const {
    data,
    networkStatus,
    loading,
    error: eventError,
  } = useEventQuery({
    variables: { id: eventId! },
    skip: !eventId,
  });

  const isLoading = isQueryLoading({ data, networkStatus, loading });

  const [upsertEvent, { error }] = useUpsertEventMutation();

  const formik = useFormik<{ actions: EventInput[] }>({
    initialValues: {
      actions: data?.event
        ? [data.event]
        : [
            {
              notes: '',
              assignedTo: { text: '' },
              dueDate: '',
              completionStatus: EventCompletionStatus.Open,
              completedAt: null,
            },
          ],
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      for (const action of values.actions) {
        const { userAdd } = selectUserOption({
          usersOptios,
          selectedUser: action.assignedTo,
        });

        let completedAt = null;
        if (action.completionStatus === EventCompletionStatus.Done) {
          if (action.completedAt) {
            completedAt = action.completedAt;
          } else {
            completedAt = moment().toDate().toISOString();
          }
        }

        await upsertEvent({
          variables: {
            workspaceId,
            eventData: [
              {
                id: eventId,
                eventType: EventKind.Action,
                notes: action.notes,
                assignedTo: _.omit(userAdd, '__typename'),
                dueDate: action.dueDate,
                objectType,
                objectId,
                completionStatus: action.completionStatus,
                completedAt,
              },
            ],
          },
        });
      }
      refetchQuery({ include: ['events', 'event'] });
      queryCache.invalidateQueries();
      onClose();
    },
  });

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={formik.dirty || isCsvDirty}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen center={true}>
          <Spacer $px={40} />
          <div className="h-padding">
            <Poppins className="m-title" px={28}>
              {eventId ? 'Edit Action' : 'Add Actions'}
            </Poppins>

            <ModalSubTitle title={objectType} value={name} />
            <Spacer $px={30} />
            {isLoading ? (
              <ActionsModalSkeleton />
            ) : (
              <>
                {!eventId && (
                  <>
                    <TabsMenu
                      tabs={[
                        { label: 'Enter Action Details', value: 'from_new', dataCy: 'from_new' },
                        { label: 'Import From CSV', value: 'from_csv', dataCy: 'from_csv' },
                      ]}
                      activeTab={activeTab}
                      setActiveTab={(tab) => {
                        setActiveTab(tab);
                        // mpEvent(MPEvents.ButtonClick, {
                        //   button:
                        //     tab === 'from_new'
                        //       ? 'Tab:Enter Control Details'
                        //       : tab === 'from_existing'
                        //         ? 'Tab:Link Exisiting Controls'
                        //         : 'Tab:Link Suggested Controls',
                        //   modal: 'Link controls to scenario modal',
                        //   tags: ['SCENARIO', 'RMP'],
                        // });
                      }}
                    />
                    <Spacer $px={40} />
                  </>
                )}
                {activeTab === 'from_new' && (
                  <FormikProvider value={formik}>
                    <FromNew eventId={eventId} />
                  </FormikProvider>
                )}
                {activeTab === 'from_csv' && (
                  <FromCSV
                    onClose={onClose}
                    setIsCsvDirty={setIsCsvDirty}
                    objectType={objectType}
                    objectId={objectId}
                    state={{
                      file,
                      setFile,
                      readyForUpload,
                      setReadyForUpload,
                    }}
                    eventIds={eventIds}
                  />
                )}
              </>
            )}
          </div>
          {(error || eventError) && (
            <ErrorLabel>{error?.message || eventError?.message || 'Something went wrong'}</ErrorLabel>
          )}
          <Spacer $px={40} />
        </StyledModal>
      }
    />
  );
};
